import { useCurrencyNumberFormatter } from 'hooks';
import useHasCostPermission from 'hooks/permission/useHasCostPermission';
import useMultiEquipmentTrackingEnabled from 'hooks/settings/useMultiEquipmentTrackingEnabled/useMultiEquipmentTrackingEnabled';
import { Duration } from 'luxon';
import { convertDurationToHoursDecimal, secondsToHmmString } from 'utils/dateUtils';
import { t } from 'utils/localize';
import { getEquipmentDisplay } from 'utils/stringUtils';
import { IEquipmentActivityTableRowInfo } from './useEquipmentActivity';
import useNoEquipmentName from './useNoEquipmentName';

export default function useEquipmentActivityReportExport() {
  const costFormatter = useCurrencyNumberFormatter('usd', { maximumFractionDigits: 2 })!;
  const canViewCost = useHasCostPermission();
  const noEquipmentName = useNoEquipmentName();
  const hasMultiEquipmentTracking = useMultiEquipmentTrackingEnabled();

  function getExportData<DataType extends IEquipmentActivityTableRowInfo>(
    data: DataType[],
    detailColumns?: (row: DataType) => object
  ) {
    return data.map((row) => {
      const costData: any = {};
      if (canViewCost) {
        costData[t('Regular Cost (Labor)')] = costFormatter.format(row.regularCost);
        costData[t('Overtime Cost (Labor)')] = costFormatter.format(row.overtimeCost);
        costData[t('Double Time Cost (Labor)')] = costFormatter.format(row.doubletimeCost);
        costData[t('Labor Burden')] = costFormatter.format(row.laborBurden);
        costData[t('Total Cost (Labor)')] = costFormatter.format(row.totalCost);
        costData[t('Equip. Rate')] = costFormatter.format(row.equipmentRate);
        costData[t('Equip. Cost')] = costFormatter.format(row.equipmentCost);
        costData[t('Total Cost')] = costFormatter.format(row.equipmentTotalCost);
      }

      const addOnData = {
        [t('Labor Hours')]: secondsToHmmString(row.totalHours),
        [t('Labor Hours (Decimal)')]: convertDurationToHoursDecimal(Duration.fromMillis(row.totalHoursDec * 1000), 2),
        [t('Add-On Hours')]: secondsToHmmString(row.addOnTime),
        [t('Add-On Hours (Decimal')]: convertDurationToHoursDecimal(Duration.fromMillis(row.addOnTime * 1000), 2),
      };

      const total = hasMultiEquipmentTracking ? row.totalHours + row.addOnTime : row.totalHours;

      return {
        [t('Equipment')]: row.equipment ? getEquipmentDisplay(row.equipment) : noEquipmentName,
        ...(detailColumns ? detailColumns(row) : {}),
        [t('Regular Time')]: secondsToHmmString(row.regularHours),
        [t('Regular Time (Decimal)')]: convertDurationToHoursDecimal(
          Duration.fromMillis(row.regularHoursDec * 1000),
          2
        ),
        [t('Overtime')]: secondsToHmmString(row.overtimeHours),
        [t('Overtime (Decimal)')]: convertDurationToHoursDecimal(Duration.fromMillis(row.overtimeHoursDec * 1000), 2),
        [t('Double Time')]: secondsToHmmString(row.doubleTimeHours),
        [t('Double Time (Decimal)')]: convertDurationToHoursDecimal(
          Duration.fromMillis(row.doubleTimeHoursDec * 1000),
          2
        ),
        ...(hasMultiEquipmentTracking ? addOnData : {}),
        [t('Total')]: secondsToHmmString(total),
        [t('Total (Decimal)')]: convertDurationToHoursDecimal(Duration.fromMillis(total * 1000), 2),
        ...costData,
      };
    });
  }

  return getExportData;
}
